import { Box, Grid } from "@mui/material";
import * as React from "react";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import * as documentService from "../../../models/services/documentService";
import { CkEditorDialog } from "../../framework/Dialog/Dialog";
import { FormField } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/components/framework/FormField";
import { GridRow } from "../../framework/Grid/GridRow";
import { DialogButtons } from "source/components/framework/Dialog/DialogButtons";
import { Base } from "ui/Scripts/source/framework/base";
import { IDocumentTemplateListItem } from "source/models/documentTemplate/documentTemplateListItem";
import { IIdTitleDropdownItem, IdTitleDropdown } from "ui/Scripts/source/components/framework/Dropdown/IdTitleDropdown";
import { AppCkEditor } from "source/components/framework/AppCkEditor";
import { VerticalContainer } from "ui/Scripts/source/components/framework/VerticalContainer";

export interface IDocumentDocumentNewDialogProp {
    folderId: string;
    companyId: string;
    onOk: (content: string, landscaperOrientation: boolean) => void;
    onCancel: () => void;
}

export interface IDocumentDocumentNewDialogState {
    dropdownItems: IIdTitleDropdownItem[];
    items: IDocumentTemplateListItem[];
    selectedItem: IDocumentTemplateListItem;
    selectedItemContent: string;
    ckEditorDataId: number;
}

export class DocumentDocumentNewDialog extends React.Component<IDocumentDocumentNewDialogProp, IDocumentDocumentNewDialogState> {
    private editor: any = null;
    private contentCache: Record<string, string> = {};

    constructor(props: IDocumentDocumentNewDialogProp) {
        super(props);
        this.state = {
            dropdownItems: [],
            items: [],
            selectedItem: null,
            selectedItemContent: "",
            ckEditorDataId: 0
        };
    }

    // #region CkEditor
    setCkEditorContent = (value: string) => {
        this.setState({ ckEditorDataId: this.state.ckEditorDataId + 1 });
        if (!this.editor) return;
        this.editor.setData(value, {
            callback: () => {
                this.editor.resetDirty();
            }
        });
        this.setState({ ckEditorDataId: this.state.ckEditorDataId + 1 });
    };

    destroyCkEditor = () => {
        if (!this.editor) return;
        this.editor.destroy();
        this.editor = null;
    };

    handleCkEditorInstanceReady = (ev) => {
        this.editor = ev.editor;
        ev.editor.resetDirty();
        this.setCkEditorContent(this.state.selectedItemContent ?? "");
    };
    // #endregion CkEditor

    loadData = async () => {
        const props = this.props;
        const result = await AppUtils.callService(() => documentService.getDocumentDocumentNew(props.companyId, props.folderId), false);
        if (!result) return;
        let dropdownItems: IIdTitleDropdownItem[] = result.items.filter(i => i.id === Base.emptyGuid).map(i => { return { id: i.id, title: i.code }; });
        const normalItems = result.items.filter(i => i.id !== Base.emptyGuid);
        dropdownItems.push({ id: "divider1", title: Translations.DocumentTemplates, divider: true });
        dropdownItems = dropdownItems.concat(normalItems.filter(i => !!i.companyId).map(i => { return { id: i.id, title: i.code }; }));
        dropdownItems.push({ id: "divider2", title: Translations.SharedDocumentTemplates, divider: true });
        dropdownItems = dropdownItems.concat(normalItems.filter(i => !i.companyId).map(i => { return { id: i.id, title: i.code }; }));
        const state = {
            ...this.state,
            items: result.items,
            dropdownItems,
            selectedItem: result.items[0]
        };
        this.setState(state, () => {
            this.setCkEditorContent("");
        });
    };

    componentDidMount(): void {
        this.loadData();
    }
    
    async componentWillUnmount(): Promise<void> {
        this.destroyCkEditor();
    }

    async componentDidUpdate(prevProps: IDocumentDocumentNewDialogProp, prevState: IDocumentDocumentNewDialogState): Promise<void> {
        const props = this.props;
        if (prevProps.companyId === props.companyId && prevProps.folderId === props.folderId) return;
        this.loadData();
    }

    loadDocumentTemplateContent = async (selectedItem: IDocumentTemplateListItem) => {
        if (!selectedItem) return;
        const props = this.props;
        const cacheKey = (props.companyId ?? "") + "_" + (props.folderId ?? "") + "_" + (selectedItem.id ?? "");
        let content = this.contentCache[cacheKey];
        if (Base.isNullOrUndefined(content)) {
            const result = await AppUtils.callService(() => documentService.getDocumentDocumentTemplateContent(props.companyId, props.folderId, selectedItem.id, selectedItem.companyId ? 0 : 1), false);
            if (!result) return;
            content = result.content;
            this.contentCache[cacheKey] = content;
        }
        this.setState({
            selectedItemContent: content
        });
        this.setCkEditorContent(content);
    };

    handleChangeDocumentTemplate = (id: string) => {
        if (!id) return;
        const state = this.state;
        const selectedItem = state.items.find(i => i.id === id);
        if (!selectedItem) return;
        this.setState({
            selectedItem
        });
        this.loadDocumentTemplateContent(selectedItem);
    };

    handleOk = async (): Promise<void> => {
        this.props.onOk(this.state.selectedItemContent, this.state.selectedItem.landscapeOrientation);
    };

    render() {
        const props = this.props;
        const state = this.state;
        if (!state.selectedItem) return null;
        return (
            <CkEditorDialog
                id="documentDocumentNewDialog"
                maxWidth="xl"
                maxHeight="100%"
                title={Translations.AddEditableDocument}
                body={
                    <Box display="flex" flexDirection="column" height="100%">
                        <GridRow height="calc(100% + 16px)" spacing={2}>
                            <Grid item sm={3} height="100%">
                                <VerticalContainer
                                    before={<>
                                        <FormField title={Translations.DocumentTemplate} fullWidth margin="0">
                                            <IdTitleDropdown
                                                selectedId={state.selectedItem?.id}
                                                items={state.dropdownItems}
                                                onChange={this.handleChangeDocumentTemplate}
                                            />
                                        </FormField>
                                        <FormField title={Translations.Description} fullWidth>
                                        </FormField>
                                    </>}
                                    main={
                                        <Box sx={{ height: "100%", overflowY: "auto" }}>
                                            {state.selectedItem.comment}
                                        </Box>
                                    }
                                />
                            </Grid>
                            <Grid item sm={9} height="100%">
                                <VerticalContainer
                                    before={
                                        <FormField title={Translations.Preview} fullWidth margin="0">
                                        </FormField>
                                    }
                                    main={
                                        <AppCkEditor
                                            fitToContainerHeight
                                            readOnly={true}
                                            value={state.selectedItemContent}
                                            dataId={state.ckEditorDataId}
                                            height=""
                                            onInstanceReady={this.handleCkEditorInstanceReady}
                                            documentLandscapeOrientation={state.selectedItem?.landscapeOrientation ?? false}
                                            onChange={null}
                                        />
                                    }
                                />
                            </Grid>
                        </GridRow>
                    </Box>
                }
                actions={
                    <Box display="flex" justifyContent="end" width="100%">
                        <DialogButtons
                            isNew={true}
                            saveTitle={Translations.Next}
                            onCancel={props.onCancel}
                            onSave={this.handleOk}
                        />
                    </Box>
                }
                onClose={props.onCancel}
            />
        );
    }
}